import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Container,
  Breadcrumbs,
  Link,
  Stack,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  Home as HomeIcon,
  LockReset as LockResetIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import Layout from './Layout';

const AdminPanel = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [filteredUsuarios, setFilteredUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [adminFilter, setAdminFilter] = useState('todos');
  
  // Estados para reset de senha
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [resetUserId, setResetUserId] = useState(null);
  const [resetUserName, setResetUserName] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState(null);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [novaSenha, setNovaSenha] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Função utilitária para status
  function getAtivo(usuario) {
    if ('ativo' in usuario) return usuario.ativo === true || usuario.ativo === 1 || usuario.ativo === "1";
    if ('Ativo' in usuario) return usuario.Ativo === true || usuario.Ativo === 1 || usuario.Ativo === "1";
    return false;
  }
  // Função utilitária para admin
  function getAdmin(usuario) {
    if ('is_admin' in usuario) return usuario.is_admin === true || usuario.is_admin === 1 || usuario.is_admin === "1";
    if ('isAdmin' in usuario) return usuario.isAdmin === true || usuario.isAdmin === 1 || usuario.isAdmin === "1";
    if ('admin' in usuario) return usuario.admin === true || usuario.admin === 1 || usuario.admin === "1";
    if ('Admin' in usuario) return usuario.Admin === true || usuario.Admin === 1 || usuario.Admin === "1";
    return false;
  }

  // Definindo filterUsuarios com useCallback antes de usá-lo no useEffect
  const filterUsuarios = useCallback(() => {
    let result = [...usuarios];

    // Filtrar por termo de busca
    if (searchTerm) {
      result = result.filter(usuario => 
        (usuario.username || usuario.Username || usuario.nome || usuario.Nome || '')
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    // Filtrar por status
    if (statusFilter !== 'todos') {
      const isAtivo = statusFilter === 'ativo';
      result = result.filter(usuario => getAtivo(usuario) === isAtivo);
    }

    // Filtrar por tipo de usuário (admin)
    if (adminFilter !== 'todos') {
      const isAdmin = adminFilter === 'admin';
      result = result.filter(usuario => getAdmin(usuario) === isAdmin);
    }

    setFilteredUsuarios(result);
  }, [usuarios, searchTerm, statusFilter, adminFilter]);

  useEffect(() => {
    fetchUsuarios();
  }, []);

  useEffect(() => {
    // Aplicar filtros sempre que os filtros ou dados mudarem
    filterUsuarios();
  }, [usuarios, searchTerm, statusFilter, adminFilter, filterUsuarios]); // Dependências diretas para evitar loops

  const fetchUsuarios = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Verificar se está autenticado
      if (!token) {
        window.location.href = '/login';
        return;
      }
      
      const response = await api.get('/usuarios');
      
      console.log('Resposta da API de usuários:', response.data);
      
      // Adicionar log detalhado dos usuários recebidos
      if (Array.isArray(response.data)) {
        response.data.forEach((u, i) => console.log(`Usuário[${i}]:`, u));
      } else if (Array.isArray(response.data.usuarios)) {
        response.data.usuarios.forEach((u, i) => console.log(`Usuário[${i}]:`, u));
      } else if (typeof response.data === 'object') {
        Object.values(response.data).forEach((v, i) => {
          if (Array.isArray(v)) v.forEach((u, j) => console.log(`Usuário[${i}][${j}]:`, u));
        });
      }
      
      // Verificar a estrutura da resposta e ajustar conforme necessário
      const usuariosData = response.data.usuarios || 
                           response.data || 
                           [];
                        
      // Para depuração
      
      
      setUsuarios(Array.isArray(usuariosData) ? usuariosData : []);
      setLoading(false);
    } catch (err) {
      console.error('Erro ao buscar usuários:', err.response || err.message || err);
      setError('Erro ao carregar usuários. Por favor, tente novamente.');
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, novoStatus) => {
    try {
      const token = localStorage.getItem('token');
      
      // Verificar se está autenticado
      if (!token) {
        window.location.href = '/login';
        return;
      }
      
      await api.put(`/usuarios/${id}`, { ativo: novoStatus });
      
      // Atualizar a lista de usuários
      setUsuarios(usuarios.map(user => 
        (user.id || user.Id || user.ID) === id ? { ...user, ativo: novoStatus, Ativo: novoStatus } : user
      ));
    } catch (err) {
      console.error('Erro ao atualizar status do usuário:', err);
      setError('Erro ao atualizar usuário. Por favor, tente novamente.');
    }
  };

  const handleRefresh = () => {
    fetchUsuarios();
    setSearchTerm('');
    setStatusFilter('todos');
    setAdminFilter('todos');
  };
  
  // Funções para reset de senha
  const handleOpenResetDialog = (userId, userName) => {
    setResetUserId(userId);
    setResetUserName(userName);
    setResetError(null);
    setResetSuccess(false);
    setNovaSenha('');
    setOpenResetDialog(true);
  };
  
  const handleCloseResetDialog = () => {
    setOpenResetDialog(false);
  };
  
  const handleResetSenha = async () => {
    try {
      setResetLoading(true);
      setResetError(null);
      
      const token = localStorage.getItem('token');
      
      // Verificar se está autenticado
      if (!token) {
        window.location.href = '/login';
        return;
      }
      
      const response = await api.post(`/usuarios/${resetUserId}/reset-senha`);
      

      
      setNovaSenha(response.data.nova_senha);
      setResetSuccess(true);
      
      // Atualizar a lista de usuários
      fetchUsuarios();
      
    } catch (err) {
      console.error('Erro ao resetar senha:', err);
      setResetError(err.response?.data?.message || 'Erro ao resetar senha. Por favor, tente novamente.');
    } finally {
      setResetLoading(false);
    }
  };
  
  const handleCopiarSenha = async () => {

    
    // PRIORIDADE 1: Clipboard API moderna (mais confiável)
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(novaSenha);

        setSnackbarMessage('✅ Senha copiada para a área de transferência');
        setSnackbarOpen(true);
        return;
      } catch (err) {
        console.warn('❌ Clipboard API falhou:', err);
      }
    }
    
    // PRIORIDADE 2: Método de seleção visual + execCommand
    try {

      
      // Encontrar o campo de senha no modal que está sendo exibido
      const passwordField = document.querySelector('input[value="' + novaSenha + '"]');
      
      if (passwordField) {
        // Método: selecionar campo existente
        passwordField.focus();
        passwordField.select();
        passwordField.setSelectionRange(0, passwordField.value.length);
        
        // Aguardar seleção
        setTimeout(() => {
          const copySuccess = document.execCommand('copy');

          
          if (copySuccess) {
            setSnackbarMessage('✅ Senha copiada para a área de transferência');
          } else {
            setSnackbarMessage('📋 Senha selecionada - use Ctrl+C para copiar');
          }
          setSnackbarOpen(true);
        }, 50);
        
      } else {
        // Método: criar input temporário VISÍVEL

        
        const tempInput = document.createElement('input');
        tempInput.type = 'text';
        tempInput.value = novaSenha;
        tempInput.style.position = 'fixed';
        tempInput.style.top = '50%';
        tempInput.style.left = '50%';
        tempInput.style.transform = 'translate(-50%, -50%)';
        tempInput.style.zIndex = '999999';
        tempInput.style.background = 'white';
        tempInput.style.border = '2px solid #007bff';
        tempInput.style.borderRadius = '4px';
        tempInput.style.padding = '10px';
        tempInput.style.fontSize = '16px';
        tempInput.style.fontFamily = 'monospace';
        tempInput.style.width = '300px';
        tempInput.readOnly = true;
        
        document.body.appendChild(tempInput);
        
        // Selecionar o texto
        tempInput.focus();
        tempInput.select();
        tempInput.setSelectionRange(0, tempInput.value.length);
        
        // Tentar copiar
        setTimeout(() => {
          const copySuccess = document.execCommand('copy');

          
          // Remover input após 2 segundos
          setTimeout(() => {
            document.body.removeChild(tempInput);
          }, 2000);
          
          if (copySuccess) {
            setSnackbarMessage('✅ Senha copiada! (Campo temporário removido em 2s)');
          } else {
            setSnackbarMessage('📋 Senha selecionada no campo - use Ctrl+C agora!');
          }
          setSnackbarOpen(true);
        }, 100);
      }
      
    } catch (error) {
      console.error('❌ Erro na cópia:', error);
      setSnackbarMessage('❌ Erro ao copiar. Tente selecionar manualmente');
      setSnackbarOpen(true);
    }
  };
  
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <Layout>
      <Container maxWidth="lg">
        {/* Cabeçalho e navegação */}
        <Box sx={{ mb: 4 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link 
              underline="hover" 
              color="inherit" 
              href="/dashboard"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Início
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="/admin"
            >
              Administração
            </Link>
            <Typography color="text.primary">Usuários</Typography>
          </Breadcrumbs>
          
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h5" gutterBottom>
                Gestão de Usuários
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Gerencie os usuários do sistema, seus perfis e permissões
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="success"
              onClick={() => window.location.href = '/novo-usuario'}
              startIcon={<AddIcon />}
            >
              Novo Usuário
            </Button>
          </Stack>
        </Box>
        
        {/* Filtros */}
        <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <TextField
              placeholder="Buscar usuários..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              size="small"
              sx={{ flexGrow: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
            />
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="ativo">Ativos</MenuItem>
                <MenuItem value="inativo">Inativos</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="admin-filter-label">Perfil</InputLabel>
              <Select
                labelId="admin-filter-label"
                value={adminFilter}
                onChange={(e) => setAdminFilter(e.target.value)}
                label="Perfil"
              >
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="admin">Administradores</MenuItem>
                <MenuItem value="usuario">Usuários</MenuItem>
              </Select>
            </FormControl>
            
            <Tooltip title="Atualizar">
              <IconButton onClick={handleRefresh} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          
          {/* Resumo dos filtros aplicados */}
          {(searchTerm || statusFilter !== 'todos' || adminFilter !== 'todos') && (
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                <FilterListIcon fontSize="small" sx={{ mr: 0.5 }} /> 
                Filtros aplicados:
              </Typography>
              
              {searchTerm && (
                <Chip 
                  label={`Busca: ${searchTerm}`} 
                  size="small" 
                  onDelete={() => setSearchTerm('')} 
                />
              )}
              
              {statusFilter !== 'todos' && (
                <Chip 
                  label={`Status: ${statusFilter === 'ativo' ? 'Ativos' : 'Inativos'}`} 
                  size="small" 
                  onDelete={() => setStatusFilter('todos')} 
                />
              )}
              
              {adminFilter !== 'todos' && (
                <Chip 
                  label={`Perfil: ${adminFilter === 'admin' ? 'Administradores' : 'Usuários'}`} 
                  size="small" 
                  onDelete={() => setAdminFilter('todos')} 
                />
              )}
            </Box>
          )}
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Tabela de dados */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredUsuarios.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            {searchTerm || statusFilter !== 'todos' || adminFilter !== 'todos' ? (
              <>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Nenhum usuário corresponde aos filtros
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tente ajustar os critérios de busca ou limpar os filtros
                </Typography>
                <Button 
                  variant="outlined" 
                  sx={{ mt: 2 }} 
                  onClick={handleRefresh}
                >
                  Limpar filtros
                </Button>
              </>
            ) : (
              <>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Nenhum usuário cadastrado
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Comece adicionando um novo usuário ao sistema
                </Typography>
                <Button 
                  variant="contained" 
                  onClick={() => window.location.href = '/novo-usuario'}
                  startIcon={<AddIcon />}
                >
                  Novo Usuário
                </Button>
              </>
            )}
          </Paper>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Exibindo {filteredUsuarios.length} de {usuarios.length} usuários
            </Typography>
            
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell width="10%" sx={{ fontWeight: 'bold' }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Username</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                    <TableCell width="15%" sx={{ fontWeight: 'bold' }}>Perfil</TableCell>
                    <TableCell width="15%" sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell width="20%" align="center" sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsuarios.map((usuario) => (
                      <TableRow 
                        key={usuario.id || usuario.Id || usuario.ID}
                        sx={{ 
                          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                        }}
                      >
                        <TableCell>{usuario.id || usuario.Id || usuario.ID}</TableCell>
                        <TableCell>{usuario.username || usuario.Username || usuario.nome || usuario.Nome}</TableCell>
                        <TableCell>{usuario.email || '-'}</TableCell>
                        <TableCell>
                          <Chip 
                            label={getAdmin(usuario) ? "Administrador" : "Usuário"} 
                            color={getAdmin(usuario) ? "primary" : "default"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={getAtivo(usuario) ? "Ativo" : "Inativo"} 
                            color={getAtivo(usuario) ? "success" : "error"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <Button 
                              size="small" 
                              variant="outlined"
                              color={getAtivo(usuario) ? "error" : "success"}
                              onClick={() => handleStatusChange(usuario.id || usuario.Id || usuario.ID, !getAtivo(usuario))}
                            >
                              {getAtivo(usuario) ? 'Desativar' : 'Ativar'}
                            </Button>
                            <Button 
                              size="small" 
                              variant="outlined"
                              color="primary"
                              onClick={() => window.location.href = `/editar-usuario/${usuario.id || usuario.Id || usuario.ID}`}
                            >
                              Editar
                            </Button>
                            <Tooltip title="Resetar Senha">
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                startIcon={<LockResetIcon />}
                                onClick={() => handleOpenResetDialog(
                                  usuario.id || usuario.Id || usuario.ID,
                                  usuario.username || usuario.Username || usuario.nome || usuario.Nome
                                )}
                              >
                                Resetar
                              </Button>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Container>
      
      {/* Modal de confirmação de reset de senha */}
      <Dialog open={openResetDialog} onClose={handleCloseResetDialog}>
        <DialogTitle>
          {resetSuccess ? 'Senha Resetada com Sucesso' : 'Confirmar Reset de Senha'}
        </DialogTitle>
        <DialogContent>
          {resetError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {resetError}
            </Alert>
          )}
          
          {!resetSuccess ? (
            <DialogContentText>
              Tem certeza que deseja resetar a senha do usuário <strong>{resetUserName}</strong>?
              <br /><br />
              Uma nova senha segura será gerada automaticamente e o usuário será obrigado a alterá-la no próximo login.
            </DialogContentText>
          ) : (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                A senha do usuário <strong>{resetUserName}</strong> foi resetada com sucesso.
                <br /><br />
                A nova senha é:
              </DialogContentText>
              
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                mb: 2
              }}>
                <TextField
                  value={novaSenha}
                  InputProps={{
                    readOnly: true,
                    style: {
                      fontFamily: 'monospace',
                      fontSize: '1.1rem',
                      fontWeight: 'bold',
                      backgroundColor: '#f8f9fa'
                    }
                  }}
                  onClick={(e) => {
                    e.target.select();
                    e.target.setSelectionRange(0, e.target.value.length);
                  }}
                  fullWidth
                  variant="outlined"
                  size="small"
                  label="Nova Senha"
                />
                <Tooltip title="Copiar senha">
                  <IconButton onClick={handleCopiarSenha} color="primary" size="large">
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              
              <Alert severity="info">
                Esta senha é válida apenas para o primeiro acesso.
                O usuário será obrigado a criar uma nova senha ao fazer login.
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!resetSuccess ? (
            <>
              <Button onClick={handleCloseResetDialog} disabled={resetLoading}>
                Cancelar
              </Button>
              <Button 
                onClick={handleResetSenha} 
                color="warning"
                disabled={resetLoading}
                variant="contained"
              >
                {resetLoading ? 'Processando...' : 'Confirmar Reset'}
              </Button>
            </>
          ) : (
            <Button onClick={handleCloseResetDialog} color="primary" variant="contained">
              Fechar
            </Button>
          )}
        </DialogActions>
      </Dialog>
      
      {/* Alerta de senha copiada */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
      />
    </Layout>
  );
};

export default AdminPanel;
